/**
 * Records Scene: displays the top 5 best game runs.
 *
 * Accessible from the TitleScene menu. Shows ranked records with
 * score, level, coins, agmuths, and time played. Persistent data
 * is read from localStorage via records-manager.js.
 */

import Phaser from 'phaser';
import { SCENE_RECORDS, SCENE_TITLE } from '../utils/constants.js';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  COLOR_WHITE,
  BORDER_MARGIN,
  AUDIO_ESC_PRESSED,
  COLOR_RECORDS_GOLD,
  COLOR_RECORDS_SILVER,
  COLOR_RECORDS_BRONZE,
} from '../config/game-config.js';
import { getSfxVol, applyMasterMute, refreshConfig } from '../utils/audio-config.js';
import { getRecords } from '../utils/records-manager.js';

// --- Text styles (module-level) ---

const HEADER_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '22px',
  color: '#ffffff',
  letterSpacing: 3,
};

const RANK_STYLES = [
  { fontFamily: '"Courier New", Courier, monospace', fontSize: '16px', color: '#ffd700' },   // 1st - gold
  { fontFamily: '"Courier New", Courier, monospace', fontSize: '16px', color: '#c0c0c0' },   // 2nd - silver
  { fontFamily: '"Courier New", Courier, monospace', fontSize: '16px', color: '#cd7f32' },   // 3rd - bronze
  { fontFamily: '"Courier New", Courier, monospace', fontSize: '16px', color: '#aaaaaa' },   // 4th
  { fontFamily: '"Courier New", Courier, monospace', fontSize: '16px', color: '#aaaaaa' },   // 5th
];

const DETAIL_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#999999',
};

const EMPTY_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '14px',
  color: '#555555',
};

const HINT_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#666666',
};

// Layout
const CX = GAME_WIDTH / 2;
const CONTENT_LEFT = BORDER_MARGIN + 50;
const LIST_START_Y = 130;
const ROW_HEIGHT = 80;

export default class RecordsScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENE_RECORDS });
  }

  create() {
    this.cameras.main.setBackgroundColor('#000000');
    this.cameras.main.fadeIn(400, 0, 0, 0);

    refreshConfig();
    applyMasterMute(this.sound);

    this._transitioning = false;

    this._drawBorder();
    this._drawContent();
    this._setupInput();
  }

  // ---------------------------------------------------------------
  // Content
  // ---------------------------------------------------------------

  /** @private */
  _drawContent() {
    const records = getRecords();

    // Header
    this.add.text(CX, 55, 'BEST GAMES', HEADER_STYLE).setOrigin(0.5);

    // Separator line
    const sep = this.add.graphics();
    sep.lineStyle(1, COLOR_WHITE, 0.15);
    sep.lineBetween(CONTENT_LEFT - 10, 100, GAME_WIDTH - CONTENT_LEFT + 10, 100);

    if (records.length === 0) {
      this.add.text(CX, GAME_HEIGHT / 2 - 20, 'No records yet.', EMPTY_STYLE).setOrigin(0.5);
      this.add.text(CX, GAME_HEIGHT / 2 + 10, 'Play a game to set your first record!', {
        ...EMPTY_STYLE,
        fontSize: '11px',
      }).setOrigin(0.5);
    } else {
      records.forEach((rec, i) => {
        const y = LIST_START_Y + i * ROW_HEIGHT;
        const rankStyle = RANK_STYLES[i] || RANK_STYLES[4];

        // Rank number
        this.add.text(CONTENT_LEFT - 25, y, `#${i + 1}`, rankStyle);

        // Score (main stat)
        this.add.text(CONTENT_LEFT + 10, y, `Score: ${rec.score.toLocaleString()}`, rankStyle);

        // Details line 1
        const levelStr = `Level ${rec.level}`;
        const coinsStr = `${rec.coins} coins`;
        const agmuthStr = `${rec.agmuths ?? 0} agmuths`;
        this.add.text(CONTENT_LEFT + 10, y + 22,
          `${levelStr}  |  ${coinsStr}  |  ${agmuthStr}`, DETAIL_STYLE);

        // Details line 2 — time played + date
        const timeStr = this._formatTime(rec.timePlayed ?? 0);
        const dateStr = rec.timestamp ? this._formatDate(rec.timestamp) : '';
        this.add.text(CONTENT_LEFT + 10, y + 38,
          `Time: ${timeStr}${dateStr ? `  |  ${dateStr}` : ''}`, DETAIL_STYLE);

        // Separator between records
        if (i < records.length - 1) {
          const sepY = y + ROW_HEIGHT - 10;
          const s = this.add.graphics();
          s.lineStyle(1, COLOR_WHITE, 0.07);
          s.lineBetween(CONTENT_LEFT, sepY, GAME_WIDTH - CONTENT_LEFT, sepY);
        }
      });
    }

    // Hint
    const hint = this.add.text(CX, GAME_HEIGHT - BORDER_MARGIN - 20,
      'ESC to return', HINT_STYLE).setOrigin(0.5);
    this.tweens.add({
      targets: hint,
      alpha: 0.3,
      duration: 700,
      yoyo: true,
      repeat: -1,
    });
  }

  /**
   * Format seconds into MM:SS or HH:MM:SS.
   * @private
   * @param {number} totalSeconds
   * @returns {string}
   */
  _formatTime(totalSeconds) {
    const secs = Math.floor(totalSeconds);
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    if (h > 0) {
      return `${h}h ${String(m).padStart(2, '0')}m ${String(s).padStart(2, '0')}s`;
    }
    return `${m}m ${String(s).padStart(2, '0')}s`;
  }

  /**
   * Format a timestamp into a short date string.
   * @private
   * @param {number} timestamp
   * @returns {string}
   */
  _formatDate(timestamp) {
    try {
      const d = new Date(timestamp);
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (_) {
      return '';
    }
  }

  // ---------------------------------------------------------------
  // Input
  // ---------------------------------------------------------------

  /** @private */
  _setupInput() {
    this.input.keyboard.on('keydown-ESC', () => this._returnToTitle());
    this.input.keyboard.on('keydown-ENTER', () => this._returnToTitle());
    this.input.keyboard.on('keydown-SPACE', () => this._returnToTitle());
  }

  // ---------------------------------------------------------------
  // Transitions
  // ---------------------------------------------------------------

  /** @private */
  _returnToTitle() {
    if (this._transitioning) return;
    this._transitioning = true;

    try {
      this.sound.play(AUDIO_ESC_PRESSED, { volume: getSfxVol(0.4) });
    } catch (_) { /* audio fallback */ }

    this.cameras.main.fadeOut(400, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(SCENE_TITLE);
    });
  }

  // ---------------------------------------------------------------
  // Border
  // ---------------------------------------------------------------

  /** @private */
  _drawBorder() {
    const g = this.add.graphics();
    g.lineStyle(1, COLOR_WHITE, 0.2);
    g.strokeRect(BORDER_MARGIN, BORDER_MARGIN,
      GAME_WIDTH - BORDER_MARGIN * 2, GAME_HEIGHT - BORDER_MARGIN * 2);

    const cornerSize = 10;
    const m = BORDER_MARGIN + 5;
    g.lineStyle(1, COLOR_WHITE, 0.4);

    // Top-left
    g.lineBetween(m, m, m + cornerSize, m);
    g.lineBetween(m, m, m, m + cornerSize);
    // Top-right
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m - cornerSize, m);
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m, m + cornerSize);
    // Bottom-left
    g.lineBetween(m, GAME_HEIGHT - m, m + cornerSize, GAME_HEIGHT - m);
    g.lineBetween(m, GAME_HEIGHT - m, m, GAME_HEIGHT - m - cornerSize);
    // Bottom-right
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m - cornerSize, GAME_HEIGHT - m);
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m, GAME_HEIGHT - m - cornerSize);
  }
}
